import { ReactNode, useMemo, useState } from "react";
import { Mui, MuiIcons } from "@leap4/frontend";

const { Box, Chip, Typography, TextField, Button, ButtonBase, Collapse, Checkbox, Autocomplete, InputAdornment, Tooltip, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions } = Mui;
const { Search, Bolt, Speed, HourglassBottom, DragIndicator, ExpandMore } = MuiIcons;

export const Section = ({ title, hint, error, children, action, defaultOpen = true }: { title: string; hint?: ReactNode; error?: string; children: ReactNode; action?: ReactNode; defaultOpen?: boolean }) => {
    const [open, setOpen] = useState(defaultOpen);
    return (
        <Box component="section" sx={{ minWidth: 0 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, minHeight: 44, borderBottom: 1, borderColor: "divider", mb: open ? 2 : 0 }}>
                <ButtonBase onClick={() => setOpen(!open)} aria-expanded={open} sx={{ display: "flex", alignItems: "center", gap: 1, py: 1, pr: 1, borderRadius: "6px", justifyContent: "flex-start", textAlign: "left" }}>
                    <ExpandMore aria-hidden="true" sx={{ fontSize: 20, color: "text.secondary", transition: "transform 0.15s", transform: open ? "none" : "rotate(-90deg)" }} />
                    <Typography variant="subtitle1" component="h3" sx={{ fontWeight: 600 }}>
                        {title}
                    </Typography>
                </ButtonBase>
                {hint && (
                    <Typography variant="caption" color="text.secondary" sx={{ minWidth: 0 }}>
                        {hint}
                    </Typography>
                )}
                {action && <Box sx={{ ml: "auto" }}>{action}</Box>}
            </Box>
            <Collapse in={open}>
                <Box sx={{ pl: { md: 3.5 }, pb: 1 }}>
                    {children}
                    {error && (
                        <Typography variant="caption" color="error" role="alert" sx={{ display: "block", mt: 0.75 }}>
                            {error}
                        </Typography>
                    )}
                </Box>
            </Collapse>
            {!open && error && (
                <Typography variant="caption" color="error" role="alert" sx={{ display: "block", mt: 0.75 }}>
                    {error}
                </Typography>
            )}
        </Box>
    );
};

// Roomier than MUI's small chip, and long labels wrap rather than being cut off mid-word.
const pickerChipSx = {
    height: "auto",
    minHeight: 30,
    fontSize: "0.8125rem",
    "& .MuiChip-label": { whiteSpace: "normal", py: 0.5, px: 1.25 },
};

// A fixed reading order; any group not listed sits before the long tail of custom and size fields.
const GROUP_ORDER = ["Time", "Device", "Channels", "Content", "Geography", "Advertiser"];
const LAST_GROUPS = ["Custom", "Size"];

const orderGroups = (groups: string[]): string[] => {
    const rank = (g: string) =>
        GROUP_ORDER.includes(g) ? GROUP_ORDER.indexOf(g) : LAST_GROUPS.includes(g) ? 100 + LAST_GROUPS.indexOf(g) : 50;
    return groups
        .map((g, index) => ({ g, index }))
        .sort((a, b) => rank(a.g) - rank(b.g) || a.index - b.index)
        .map(({ g }) => g);
};

export interface IPickerItem {
    key: string;
    label: string;
    group: string;
    hint?: string;
    tag?: string;
    /** Stays on the daily rollups, so it keeps a report quick. */
    fast?: boolean;
}

export interface ILockedChip {
    key: string;
    label: string;
    reason: string;
}

const FastTag = () => (
    <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.25, fontSize: "0.6875rem", fontWeight: 600, color: "success.main", whiteSpace: "nowrap" }}>
        <Bolt aria-hidden="true" sx={{ fontSize: 14 }} />
        fast
    </Box>
);

const ItemTag = ({ text }: { text: string }) => (
    <Box component="span" sx={{ fontSize: "0.6875rem", color: "text.secondary", border: 1, borderColor: "divider", borderRadius: "4px", px: 0.5, whiteSpace: "nowrap" }}>
        {text}
    </Box>
);

const LockedChips = ({ locked }: { locked: ILockedChip[] }) => (
    <>
        {locked.map((l) => (
            <Tooltip key={l.key} title={l.reason}>
                <Chip role="listitem" size="small" label={`${l.label} · locked`} sx={{ ...pickerChipSx, bgcolor: "action.selected", color: "text.secondary" }} />
            </Tooltip>
        ))}
    </>
);

const useDragOver = () => {
    const [dragOver, setDragOver] = useState<number | null>(null);
    const chipDrag = (index: number, getDragProps?: (index: number) => Record<string, unknown>) => {
        const { sx: dragSx, onDragOver, onDrop, onDragEnd, ...drag } = (getDragProps ? getDragProps(index) : {}) as Record<string, any>;
        return {
            props: {
                ...drag,
                onDragOver: (e: React.DragEvent) => { onDragOver?.(e); setDragOver(index); },
                onDragLeave: () => setDragOver((cur) => (cur === index ? null : cur)),
                onDrop: (e: React.DragEvent) => { onDrop?.(e); setDragOver(null); },
                onDragEnd: (e: React.DragEvent) => { onDragEnd?.(e); setDragOver(null); },
            },
            sx: {
                ...(dragSx || {}),
                ...(getDragProps && { "& .MuiChip-icon": { cursor: "grab", ml: 0.5, mr: -0.5, fontSize: 16, opacity: 0.8 } }),
                ...(dragOver === index && { outline: "2px dashed", outlineColor: "primary.light", outlineOffset: 2 }),
            },
        };
    };
    return chipDrag;
};

interface IChipPickerProps {
    label: string;
    items: IPickerItem[];
    selected: string[];
    onChange: (keys: string[]) => void;
    showOrder?: boolean;
    locked?: ILockedChip[];
    getDragProps?: (index: number) => Record<string, unknown>;
    note?: ReactNode;
}

const ChipGroup = ({ group, items, picked, label, forceOpen, onToggle, onSet }: { group: string; items: IPickerItem[]; picked: string[]; label: string; forceOpen: boolean; onToggle: (key: string) => void; onSet: (keys: string[], on: boolean) => void }) => {
    const [open, setOpen] = useState(false);
    const shown = open || forceOpen;
    const count = items.filter((i) => picked.includes(i.key)).length;
    return (
        <Box sx={{ minWidth: 0 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, borderBottom: 1, borderColor: "divider", minHeight: 40 }}>
                <ButtonBase onClick={() => setOpen(!open)} aria-expanded={shown} sx={{ display: "flex", alignItems: "center", gap: 1, py: 0.75, pr: 1, borderRadius: "6px", flex: 1, justifyContent: "flex-start" }}>
                    <ExpandMore aria-hidden="true" sx={{ fontSize: 18, color: "text.secondary", transition: "transform 0.15s", transform: shown ? "none" : "rotate(-90deg)" }} />
                    <Typography variant="body2" component="h4" sx={{ fontWeight: 600 }}>
                        {group}
                    </Typography>
                    <Typography variant="caption" color={count ? "primary" : "text.secondary"} sx={{ fontWeight: count ? 600 : 400 }}>
                        {count ? `${count} of ${items.length}` : items.length}
                    </Typography>
                </ButtonBase>
            </Box>
            <Collapse in={shown}>
                <Box sx={{ pl: 3.25, pt: 1, pb: 2 }}>
                    <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
                        <Button size="small" onClick={() => onSet(items.map((i) => i.key), true)} disabled={count === items.length} sx={{ px: 0.75 }}>
                            Add all
                        </Button>
                        <Button size="small" onClick={() => onSet(items.map((i) => i.key), false)} disabled={count === 0} sx={{ px: 0.75 }}>
                            Remove all
                        </Button>
                    </Box>
                    <Box role="group" aria-label={`${group} ${label.toLowerCase()}`} sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                        {[...items]
                            .sort((x, y) => Number(!!y.fast) - Number(!!x.fast))
                            .map((i) => {
                                const on = picked.includes(i.key);
                                return (
                                    <Tooltip key={i.key} title={i.hint ?? ""} disableHoverListener={!i.hint} describeChild>
                                        <Chip
                                            size="small"
                                            variant={on ? "filled" : "outlined"}
                                            color={on ? "primary" : "default"}
                                            clickable
                                            aria-pressed={on}
                                            onClick={() => onToggle(i.key)}
                                            sx={{ ...pickerChipSx, border: 1, borderColor: on ? "primary.main" : "divider" }}
                                            label={
                                                <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
                                                    {i.label}
                                                    {i.fast && <FastTag />}
                                                    {i.tag && <ItemTag text={i.tag} />}
                                                </Box>
                                            }
                                        />
                                    </Tooltip>
                                );
                            })}
                    </Box>
                </Box>
            </Collapse>
        </Box>
    );
};

/** Picked chips first (in order, draggable), then each group in its own collapsible panel. */
export const ChipPicker = ({ label, items, selected, onChange, showOrder, locked = [], getDragProps, note }: IChipPickerProps) => {
    const [query, setQuery] = useState("");
    const chipDrag = useDragOver();
    const byKey = useMemo(() => new Map(items.map((i) => [i.key, i])), [items]);
    const picked = selected.filter((k) => byKey.has(k));
    const q = query.trim().toLowerCase();
    const matches = items.filter((i) => !q || i.label.toLowerCase().includes(q) || i.group.toLowerCase().includes(q));
    const groups = orderGroups(Array.from(new Set(matches.map((i) => i.group))));

    const toggle = (key: string) => onChange(picked.includes(key) ? picked.filter((k) => k !== key) : [...picked, key]);
    const setMany = (keys: string[], on: boolean) => onChange(on ? [...picked, ...keys.filter((k) => !picked.includes(k))] : picked.filter((k) => !keys.includes(k)));
    const reorderable = !!getDragProps && picked.length > 1;

    return (
        <Box>
            <Box role="list" aria-label={`Selected ${label.toLowerCase()}`} sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1, mb: 1, minHeight: 30 }}>
                {locked.length === 0 && picked.length === 0 && (
                    <Typography variant="caption" color="text.secondary">None selected</Typography>
                )}
                <LockedChips locked={locked} />
                {picked.map((key, index) => {
                    const drag = chipDrag(index, reorderable ? getDragProps : undefined);
                    return (
                        <Chip
                            role="listitem"
                            key={key}
                            size="small"
                            color="primary"
                            icon={reorderable ? <DragIndicator aria-hidden="true" /> : undefined}
                            label={showOrder ? `${index + 1 + locked.length}. ${byKey.get(key)!.label}` : byKey.get(key)!.label}
                            onDelete={() => toggle(key)}
                            {...drag.props}
                            sx={{ ...pickerChipSx, ...drag.sx }}
                        />
                    );
                })}
                {picked.length > 0 && (
                    <Button size="small" onClick={() => onChange([])} sx={{ px: 0.75, minHeight: 30 }}>
                        Clear all
                    </Button>
                )}
            </Box>
            {getDragProps && (
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1, visibility: picked.length > 1 ? "visible" : "hidden" }}>
                    Drag the chips to change the column order.
                </Typography>
            )}
            <Box sx={{ border: 1, borderColor: "divider", borderRadius: "8px", px: 2, pt: 1, pb: 1.5 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap", py: 0.5 }}>
                    <TextField
                        size="small"
                        placeholder={`Search ${items.length} ${label.toLowerCase()}`}
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        slotProps={{ htmlInput: { "aria-label": `Search ${label.toLowerCase()}` }, input: { startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> } }}
                        sx={{ width: { xs: "100%", sm: 280 } }}
                    />
                    {note && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                            {note}
                        </Typography>
                    )}
                </Box>
                {groups.map((group) => (
                    <ChipGroup key={group} group={group} label={label} items={matches.filter((i) => i.group === group)} picked={picked} forceOpen={!!q} onToggle={toggle} onSet={setMany} />
                ))}
                {q && matches.length === 0 && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", pt: 1 }}>
                        No {label.toLowerCase()} match "{query}".
                    </Typography>
                )}
            </Box>
        </Box>
    );
};

const METRIC_GROUP_ORDER = ["Core", "Attributed events", "Events", "Calculated", "Predicted"];

/** A grouped multi-select; picked metrics show as draggable chips inside the field. */
export const MetricSelect = ({ items, selected, onChange, locked = [], getDragProps }: { items: IPickerItem[]; selected: string[]; onChange: (keys: string[]) => void; locked?: ILockedChip[]; getDragProps?: (index: number) => Record<string, unknown> }) => {
    const chipDrag = useDragOver();
    const options = useMemo(() => {
        const rank = (g: string) => (METRIC_GROUP_ORDER.includes(g) ? METRIC_GROUP_ORDER.indexOf(g) : 50);
        return items.map((item, index) => ({ item, index })).sort((a, b) => rank(a.item.group) - rank(b.item.group) || a.index - b.index).map(({ item }) => item);
    }, [items]);
    const byKey = useMemo(() => new Map(items.map((i) => [i.key, i])), [items]);
    const value = selected.map((k) => byKey.get(k)).filter((i): i is IPickerItem => !!i);
    const reorderable = !!getDragProps && value.length > 1;

    return (
        <Box>
            <Autocomplete
                multiple
                disableCloseOnSelect
                options={options}
                value={value}
                groupBy={(o: IPickerItem) => o.group}
                getOptionLabel={(o: IPickerItem) => o.label}
                isOptionEqualToValue={(a: IPickerItem, b: IPickerItem) => a.key === b.key}
                onChange={(_e: unknown, next: IPickerItem[]) => onChange(next.map((i) => i.key))}
                renderOption={({ key, ...props }: any, o: IPickerItem, { selected: on }: { selected: boolean }) => (
                    <li key={key} {...props}>
                        <Checkbox size="small" checked={on} sx={{ p: 0.5, mr: 1 }} />
                        <Box sx={{ minWidth: 0 }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                                <Typography variant="body2">{o.label}</Typography>
                                {o.tag && <ItemTag text={o.tag} />}
                            </Box>
                            {o.hint && (
                                <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                                    {o.hint}
                                </Typography>
                            )}
                        </Box>
                    </li>
                )}
                renderValue={(vals: IPickerItem[], getItemProps: any) => (
                    <>
                        <LockedChips locked={locked} />
                        {vals.map((o, index) => {
                            const { key, ...itemProps } = getItemProps({ index });
                            const drag = chipDrag(index, reorderable ? getDragProps : undefined);
                            return (
                                <Chip
                                    key={key}
                                    {...itemProps}
                                    size="small"
                                    color="primary"
                                    label={o.label}
                                    icon={reorderable ? <DragIndicator aria-hidden="true" /> : undefined}
                                    {...drag.props}
                                    onMouseDown={(e: React.MouseEvent) => e.stopPropagation()}
                                    sx={{ ...pickerChipSx, ...drag.sx }}
                                />
                            );
                        })}
                    </>
                )}
                renderInput={(params: any) => <TextField {...params} placeholder={value.length || locked.length ? "" : "Select metrics"} slotProps={{ ...params.slotProps, htmlInput: { ...params.slotProps?.htmlInput, "aria-label": "Metrics" } }} />}
                slotProps={{ listbox: { sx: { maxHeight: 420 } } }}
            />
            {getDragProps && (
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.75, visibility: reorderable ? "visible" : "hidden" }}>
                    Drag the chips to change the column order.
                </Typography>
            )}
        </Box>
    );
};

/** Icon and wording carry the level, so it never depends on colour alone. */
export const SpeedPill = ({ level, label, estimate, reason }: { level: "fast" | "moderate" | "slow"; label: string; estimate: string; reason?: string }) => {
    const tone = {
        fast: { light: "#1F6B41", dark: "#A6E3C0" },
        moderate: { light: "#4342B8", dark: "#C9C8FF" },
        slow: { light: "#8A5A00", dark: "#F2C66B" },
    }[level];
    const Icon = level === "fast" ? Bolt : level === "moderate" ? Speed : HourglassBottom;
    const color = (t: any) => (t.palette.mode === "dark" ? tone.dark : tone.light);
    return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap", minWidth: 0 }}>
            <Box
                role="status"
                aria-label={`Estimated speed: ${label}, ${estimate}`}
                sx={{ display: "inline-flex", alignItems: "center", gap: 1, pl: 0.75, pr: 1.25, py: 0.5, border: 1, borderColor: "divider", borderRadius: "8px", bgcolor: "background.paper" }}
            >
                <Box sx={{ width: 26, height: 26, borderRadius: "6px", display: "grid", placeItems: "center", bgcolor: "action.hover", color }}>
                    <Icon sx={{ fontSize: 16 }} />
                </Box>
                <Box sx={{ display: "flex", flexDirection: "column", lineHeight: 1.1 }}>
                    <Typography component="span" sx={{ fontSize: "0.6875rem", color: "text.secondary", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                        Estimated
                    </Typography>
                    <Typography component="span" sx={{ fontSize: "0.8125rem", fontWeight: 600 }}>
                        {label}
                        <Box component="span" sx={{ fontWeight: 400, color: "text.secondary", ml: 0.75 }}>
                            {estimate}
                        </Box>
                    </Typography>
                </Box>
            </Box>
            {reason && (
                <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 520 }}>
                    {reason}
                </Typography>
            )}
        </Box>
    );
};

export const ConfirmDialog = ({ open, title, body, confirmLabel, destructive, onConfirm, onCancel }: { open: boolean; title: string; body: ReactNode; confirmLabel: string; destructive?: boolean; onConfirm: () => void; onCancel: () => void }) => (
    <Dialog open={open} onClose={onCancel} slotProps={{ paper: { role: "alertdialog" } }} aria-labelledby="confirm-title">
        <DialogTitle id="confirm-title">{title}</DialogTitle>
        <DialogContent>
            <DialogContentText component="div">{body}</DialogContentText>
        </DialogContent>
        <DialogActions>
            <Button onClick={onCancel}>Cancel</Button>
            <Button variant="contained" color={destructive ? "error" : "primary"} onClick={onConfirm} autoFocus>
                {confirmLabel}
            </Button>
        </DialogActions>
    </Dialog>
);

